import { useState } from "react";
import { ChevronLeft, Eye } from "lucide-react";
import { DEMO_SERIAL, SCAN_REASONS } from "../../config/site";
import { Icon } from "../brand/Icon";
import { LogoMark } from "../brand/Logo";

const ReasonGrid = ({ onPick }) => (
  <>
    <h3 className="font-display text-2xl font-bold text-slate-50">How can we help?</h3>
    <p className="mt-1 text-xs text-slate-400">Choose a reason to contact the owner of this vehicle.</p>
    <div className="mt-5 grid grid-cols-2 gap-2.5">
      {SCAN_REASONS.map((r) => (
        <button key={r.id} type="button" data-testid={`demo-reason-${r.id}`} onClick={() => onPick(r)}
          className={`flex min-h-[88px] flex-col items-start justify-between rounded-xl border p-3 text-left text-[13px] font-semibold leading-tight transition-[background-color,border-color,transform] duration-200 active:scale-[0.97] focus-ring ${r.id === "emergency" ? "border-red-500/40 bg-red-500/10 text-red-100 hover:bg-red-500/15" : "border-white/10 bg-white/[0.04] text-slate-100 hover:border-[#FF5500]/50 hover:bg-white/[0.07]"}`}>
          <Icon name={r.icon} className={`h-5 w-5 ${r.id === "emergency" ? "text-red-400" : "text-[#FF5500]"}`} />
          {r.label}
        </button>
      ))}
    </div>
  </>
);

const ReasonDetail = ({ reason, onBack, onSend, sent }) => {
  const [note, setNote] = useState("");
  if (sent) {
    return (
      <div data-testid="demo-preview-modal" role="status" className="flex h-full flex-col items-center justify-center text-center">
        <Eye className="h-10 w-10 text-[#FF5500]" aria-hidden="true" />
        <h3 className="mt-4 font-display text-xl font-bold text-slate-50">Preview only</h3>
        <p className="mt-2 text-sm text-slate-400">This is a design prototype. No alert was sent and no owner was contacted.</p>
        <button type="button" data-testid="demo-restart-button" onClick={onBack} className="mt-6 min-h-[44px] rounded-lg border border-white/15 px-5 text-sm font-semibold text-slate-100 hover:bg-white/5 focus-ring">Start again</button>
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col">
      <button type="button" data-testid="demo-back-button" onClick={onBack} className="inline-flex min-h-[44px] items-center gap-1 self-start text-sm text-slate-400 hover:text-slate-100 focus-ring rounded">
        <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Back
      </button>
      <div className="mt-3 flex items-center gap-3">
        <Icon name={reason.icon} className="h-6 w-6 text-[#FF5500]" />
        <h3 data-testid="demo-selected-reason" className="font-display text-xl font-bold text-slate-50">{reason.label}</h3>
      </div>
      <label htmlFor="demo-note" className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-400">Add a note (optional)</label>
      <textarea id="demo-note" data-testid="demo-custom-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={4} maxLength={240}
        placeholder="e.g. I'm parked behind you near Gate 2"
        className="mt-2 w-full resize-none rounded-xl border border-white/10 bg-white/[0.04] p-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-[#FF5500]/60 focus:outline-none" />
      <button type="button" data-testid="demo-send-alert-button" onClick={onSend}
        className="mt-auto min-h-[48px] rounded-xl bg-[#FF5500] text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#FF6B1A] focus-ring">
        Notify owner (preview)
      </button>
    </div>
  );
};

export const ScannerPhone = () => {
  const [reason, setReason] = useState(null);
  const [sent, setSent] = useState(false);
  const reset = () => { setReason(null); setSent(false); };
  return (
    <div data-testid="demo-scanner-phone" className="mx-auto w-full max-w-[340px] rounded-[44px] border border-white/15 bg-[#05060A] p-3 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9)]">
      <div className="relative flex h-[620px] flex-col overflow-hidden rounded-[34px] bg-[#0B0D14] px-5 pb-6 pt-10">
        <div className="absolute left-1/2 top-3 h-5 w-24 -translate-x-1/2 rounded-full bg-black" aria-hidden="true" />
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2"><LogoMark className="h-6 w-6" /><span className="font-display text-sm font-extrabold tracking-[0.2em] text-slate-50">CARLINK</span></span>
          <span className="font-mono text-[10px] tracking-widest text-slate-500">{DEMO_SERIAL}</span>
        </div>
        <div className="mt-6 flex-1">
          {reason ? <ReasonDetail reason={reason} sent={sent} onBack={reset} onSend={() => setSent(true)} /> : <ReasonGrid onPick={setReason} />}
        </div>
        <p className="mt-4 text-center text-[10px] text-slate-500">Your contact details are not shared with the owner unless you choose to.</p>
      </div>
    </div>
  );
};
